import React from 'react';
import { useTranslation } from 'react-i18next';

import { PINNED, UNITS } from '@private/data-grid';

import { PERMISSIONS } from 'api/auth/constants';
import { IClientInstruments } from 'api/clients/types';
import { gridConfigFallback } from 'constants/common';
import { useRBAC } from 'hooks/useRBAC';
import { getGridConfigs } from 'utils/grid/getGridConfig';
import {
  renderBinaryCell,
  renderEmptyHeaderCell,
  renderHeaderCell,
  renderRowCell,
} from 'utils/grid/sharedRenderers';
import {
  IExtendedColumn,
  IGetGridConfigsResult,
  IUseGetGridData,
} from 'utils/grid/types';

import {
  ActionsCell,
  ClientIdCell,
  DictionaryCell,
  MerchantNameCell,
  MerchantTerminalNameCell,
} from './cells';

const prefix = 'clientsInstruments.columns';

export const useClientInstrumentsGridData = ({
  data,
  filters,
  hiddenColumns,
}: IUseGetGridData<IClientInstruments>): IGetGridConfigsResult<IClientInstruments> => {
  const { t } = useTranslation('translation');
  const { checkPermission } = useRBAC();

  const pageConfig = React.useMemo(() => {
    if (!data) {
      return gridConfigFallback;
    }

    const columns = [
      {
        dataKey: 'merchantTerminal.merchant.name',
        hidden: hiddenColumns?.['merchantTerminal.merchant.name'],
        minWidth: 200,
        renderHeaderCell,
        renderRowCell: MerchantNameCell,
        title: t(`${prefix}.merchantTerminal.merchant.name`),
        width: { unit: UNITS.PERCENT, value: 10 },
      },
      {
        dataKey: 'merchantTerminal.name',
        minWidth: 180,
        renderHeaderCell,
        renderRowCell: MerchantTerminalNameCell,
        title: t(`${prefix}.merchantTerminal.name`),
        width: { unit: UNITS.PERCENT, value: 10 },
      },
      {
        dataKey: 'customer.merchantCustomerId',
        minWidth: 180,
        renderHeaderCell,
        renderRowCell: ClientIdCell,
        title: t(`${prefix}.customer.merchantCustomerId`),
        width: { unit: UNITS.PERCENT, value: 10 },
      },
      {
        dataKey: 'maskedCardPan',
        minWidth: 210,
        renderHeaderCell,
        renderRowCell,
        title: t(`${prefix}.maskedCardPan`),
        width: { unit: UNITS.PERCENT, value: 15 },
      },
      {
        dataKey: 'cardPanId',
        minWidth: 180,
        renderHeaderCell,
        renderRowCell,
        title: t(`${prefix}.cardPanId`),
        width: { unit: UNITS.PERCENT, value: 10 },
      },
      {
        dataKey: 'verification',
        minWidth: 170,
        renderHeaderCell,
        renderRowCell: DictionaryCell,
        title: t(`${prefix}.verification`),
        width: { unit: UNITS.PERCENT, value: 10 },
      },
      {
        dataKey: 'customer.email',
        hidden: hiddenColumns?.['customer.email'],
        minWidth: 200,
        renderHeaderCell,
        renderRowCell,
        title: t(`${prefix}.customer.email`),
        width: { unit: UNITS.PERCENT, value: 15 },
      },
      {
        dataKey: 'cardExpiryDate',
        minWidth: 160,
        renderHeaderCell,
        renderRowCell,
        title: t(`${prefix}.cardExpiryDate`),
        width: { unit: UNITS.PERCENT, value: 15 },
      },
      {
        dataKey: 'cardholder',
        minWidth: 160,
        renderHeaderCell,
        renderRowCell,
        title: t(`${prefix}.cardholder`),
        width: { unit: UNITS.PERCENT, value: 15 },
      },
      {
        dataKey: 'cardBrand',
        minWidth: 210,
        renderHeaderCell,
        renderRowCell: DictionaryCell,
        title: t(`${prefix}.cardBrand`),
        width: { unit: UNITS.PERCENT, value: 15 },
      },
      {
        dataKey: 'cardType',
        minWidth: 160,
        renderHeaderCell,
        renderRowCell: DictionaryCell,
        title: t(`${prefix}.cardType`),
        width: { unit: UNITS.PERCENT, value: 15 },
      },
      {
        dataKey: 'cardCountry',
        minWidth: 230,
        renderHeaderCell,
        renderRowCell,
        title: t(`${prefix}.cardCountry`),
        width: { unit: UNITS.PERCENT, value: 15 },
      },
      {
        dataKey: 'binCountryName',
        minWidth: 230,
        renderHeaderCell,
        renderRowCell,
        title: t(`${prefix}.binCountryName`),
        width: { unit: UNITS.PERCENT, value: 15 },
      },
      {
        dataKey: 'cardCategory',
        minWidth: 160,
        renderHeaderCell,
        renderRowCell,
        title: t(`${prefix}.cardCategory`),
        width: { unit: UNITS.PERCENT, value: 15 },
      },
      {
        dataKey: 'cardFullBin',
        hidden: hiddenColumns?.cardFullBin,
        minWidth: 160,
        renderHeaderCell,
        renderRowCell,
        title: t(`${prefix}.cardFullBin`),
        width: { unit: UNITS.PERCENT, value: 15 },
      },
      {
        dataKey: 'cardIssuingOrganization',
        minWidth: 180,
        renderHeaderCell,
        renderRowCell,
        title: t(`${prefix}.cardIssuingOrganization`),
        width: { unit: UNITS.PERCENT, value: 15 },
      },
      {
        dataKey: 'binLength',
        minWidth: 160,
        renderHeaderCell,
        renderRowCell,
        title: t(`${prefix}.binLength`),
        width: { unit: UNITS.PERCENT, value: 15 },
      },
      {
        dataKey: 'blockedByPan',
        minWidth: 180,
        renderHeaderCell,
        renderRowCell: renderBinaryCell,
        title: t(`${prefix}.blockedByPan`),
        width: { unit: UNITS.PERCENT, value: 6 },
      },
      {
        dataKey: 'blockedByCardMask',
        minWidth: 180,
        renderHeaderCell,
        renderRowCell: renderBinaryCell,
        title: t(`${prefix}.blockedByCardMask`),
        width: { unit: UNITS.PERCENT, value: 6 },
      },
    ] as IExtendedColumn<IClientInstruments>[];

    const canBlockedByCardMask = checkPermission({
      list: [PERMISSIONS.CAN_CREATE_BLACKLISTS_CM],
    });
    const canBlockedByPan = checkPermission({
      list: [PERMISSIONS.CAN_CREATE_BLACKLISTS_CARDS_PANS],
    });
    const canChangeStatus = checkPermission({
      list: [PERMISSIONS.CAN_UPDATE_SAVED_PAYMENT_TOKEN],
    });

    const actionsCount =
      Number(canBlockedByCardMask) + Number(canBlockedByPan) + Number(canChangeStatus);

    if (actionsCount) {
      columns.push({
        dataKey: 'actions' as keyof IClientInstruments,
        minWidth: canChangeStatus && (canBlockedByCardMask || canBlockedByPan) ? 110 : 70,
        pinned: PINNED.FIXED_RIGHT,
        renderHeaderCell: renderEmptyHeaderCell,
        renderRowCell: (params) => (
          <ActionsCell
            {...params}
            actionsCount={actionsCount}
            canBlockedByCardMask={canBlockedByCardMask}
            canBlockedByPan={canBlockedByPan}
            canChangeStatus={canChangeStatus}
          />
        ),
        title: t('common.columns.actions'),
        width: { unit: UNITS.PERCENT, value: 6 },
      } as IExtendedColumn<IClientInstruments>);
    }

    return getGridConfigs({ columns, filters });
  }, [checkPermission, data, filters, hiddenColumns, t]);

  return pageConfig;
};
